<script setup lang="ts">
import {computed, onMounted, ref, watch, inject} from "vue";
import { useDisplay } from 'vuetify'
import {useCommunityDataPublic} from "@/stores/communityDataPublic";
import {useFormDataStore} from "@/stores/formDataStore";
import { getDsoListByZip } from "@/common/dsoResolver";
import { useI18n } from "vue-i18n";
import {TextField} from "@es-bill/es-bill-common-components";
import type { FormValidation } from '@/types/form-validation';
import { METERING_POINT_DIRECTION, CONNECTION_TYPE } from "@/lib/constants/meteringPoint";
import type { ConnectionType, MeteringPointDirection } from "@/lib/constants/meteringPoint";

const { width } = useDisplay()
const { t } = useI18n();

interface Props {
  indexNumber?: number;
  meteringPointModel?: {
    nameFriendly: string;
    meteringPoint: string;
    meteringPointDirection: MeteringPointDirection;
    ecnDso?: string;
    participationFactor?: number;
    addressPostal: {
      name: string;
      address: string;
      addressExtra: string;
      zip: string;
      city: string;
      country: string;
    };
    assumptionEnergyPerYearKwh: string;
    energyStorageKwh: string;
    plant: {
      generationConfiguration: ConnectionType;
      lineCapacity: string;
      generationType: string;
    };
  };
}

const props = defineProps<Props>()
const emit = defineEmits(['update:meteringPointModel'])

const formDataStore = useFormDataStore();
const communityDataPublicStore = useCommunityDataPublic();
const formValidation = inject('$formValidation') as FormValidation;

const communityActive = computed(() => communityDataPublicStore.communityActive);
const multiDsoMode = computed(()=> communityActive.value?.communityType === '16b');

// Create a local editable copy to avoid mutating props
const meteringPointModelLocal = ref<Props['meteringPointModel']>(
  props.meteringPointModel ? JSON.parse(JSON.stringify(props.meteringPointModel)) : undefined
);

const modeProducer = computed(() => meteringPointModelLocal.value?.meteringPointDirection === METERING_POINT_DIRECTION.GENERATION);
const modeConsumer = computed(() => meteringPointModelLocal.value?.meteringPointDirection === METERING_POINT_DIRECTION.CONSUMPTION);

// Ensure default participationFactor on local model
if (meteringPointModelLocal.value && meteringPointModelLocal.value.participationFactor === undefined) {
  // default to 100
  // allow UI to coerce as needed
  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
  // @ts-ignore
  meteringPointModelLocal.value.participationFactor = 100;
}

// add uniqueness rule for metering point numbers
const uniqueMeteringPointRule = (value: string) => {
  const allNumbers = formDataStore.formData.meteringPointsAssociated.map((mp: { meteringPoint: string }) => mp.meteringPoint);
  const count = allNumbers.filter((n: string) => n === value).length;
  return count <= 1 || t('validation.duplicateMeteringPointNumber');
};

watch(
  () => meteringPointModelLocal.value?.addressPostal?.zip,
  (zip: string | undefined) => {
    if(!zip || zip.length !== 4 || isNaN(Number(zip))) {
      itemsDso.value = [];
      if (meteringPointModelLocal.value) {
        meteringPointModelLocal.value.ecnDso = "";
      }
      return // only use complete numbers
    }

    itemsDso.value = getDsoChoices(zip);
  }
)

function getDsoChoices(zip: string) {
  return getDsoListByZip(zip)
}


const itemsDso = ref<any[]>([]);
const itemsDsoSelectable = computed(() => {
  const zip = meteringPointModelLocal.value?.addressPostal?.zip;
  return !!zip && zip.length === 4 && !isNaN(Number(zip));
});

onMounted(() => {
  if(itemsDsoSelectable.value && meteringPointModelLocal.value?.addressPostal?.zip) // initial assignment
    itemsDso.value = getDsoChoices(meteringPointModelLocal.value.addressPostal.zip);
})

const itemsMeteringPointDirection = [
  { title: "Bezug", value: "CONSUMPTION"},
  { title: "Einspeisung/Erzeugung", value: "GENERATION"},
];

const itemsGenerationType = computed(() => [
  { title: t('generationTypes.photovoltaics'), value: "Photovoltaik (PV)"},
  { title: t('generationTypes.hydroPower'), value: "Wasserkraft"},
  { title: t('generationTypes.windPower'), value: "Windkraft"},
  { title: t('generationTypes.biogas'), value: "Biogas/Biomasse mit Verstromung"},
]);

const useBillingAddress = ref(false)
watch(() => useBillingAddress.value,
  (uba: boolean) => {

    // copy data on activation
    if(uba && meteringPointModelLocal.value) {
      meteringPointModelLocal.value.addressPostal.address=
        formDataStore.formData.billingData.addressPostal.address;
      meteringPointModelLocal.value.addressPostal.addressExtra=
        formDataStore.formData.billingData.addressPostal.addressExtra;
      meteringPointModelLocal.value.addressPostal.zip=
        formDataStore.formData.billingData.addressPostal.zip;
      meteringPointModelLocal.value.addressPostal.city=
        formDataStore.formData.billingData.addressPostal.city;
    }
  })


const haveStorage = ref(false)
watch(() => haveStorage.value,
  (hs: boolean) => {

    if(!hs && meteringPointModelLocal.value) { // reset saved value
      meteringPointModelLocal.value.energyStorageKwh="";
    }
  })

// Prop <-> local sync
watch(
  () => props.meteringPointModel,
  (val: Props['meteringPointModel']) => {
    if (val == null) {
      meteringPointModelLocal.value = undefined;
      return;
    }

    if (!meteringPointModelLocal.value) {
      meteringPointModelLocal.value = JSON.parse(JSON.stringify(val));
      return;
    }

    // Keep the same root reference to avoid remounting inputs; update fields in place
    Object.assign(
      meteringPointModelLocal.value,
      JSON.parse(JSON.stringify(val))
    );
  },
  // shallow watcher to avoid frequent deep syncs during typing
)

watch(
  meteringPointModelLocal,
  (val: Props['meteringPointModel']) => {
    emit('update:meteringPointModel', val);
  },
  { deep: true, flush: 'post' }
)

</script>

<template>
  <v-row no-gutters>
    <v-col cols="12">
      <v-row class="formRow" no-gutters>
        <v-col cols="12" sm="6" class="formInput6">
          <TextField :label="t('meteringPointLabels.designation')"
                      :hint="t('descriptionHint')"
                      :rules="[formValidation.formValidationRequired]"
                      v-model="meteringPointModelLocal!.nameFriendly"
          ></TextField>
        </v-col>
        <v-col cols="12" sm="8" class="formInput8">
          <TextField :label="t('meteringPointLabels.meteringPointNumber')"
                      :rules="[formValidation.formValidationRequired, formValidation.formValidationMeteringPoint, uniqueMeteringPointRule]"
                      :hint="t('meteringPointHint')"
                      v-model="meteringPointModelLocal!.meteringPoint"></TextField>
        </v-col>
        <v-col cols="12" sm="4" class="formInput4">
          <v-select
              :label="t('meteringPointLabels.energyDirection')"
              variant="outlined"
              color="dark-grey-2"
              :items="itemsMeteringPointDirection"
              v-model="meteringPointModelLocal!.meteringPointDirection"
              :rules="[formValidation.formValidationRequired]"
          ></v-select>
        </v-col>
        <v-col cols="12" sm="4" class="formInput4">
          <TextField :label="t('participationFactor')" suffix="%"
                      :rules="[formValidation.formValidationIsPercentage]"
                      v-model="meteringPointModelLocal!.participationFactor"
          ></TextField>
        </v-col>
        <v-col cols="12" sm="7" class="formInput7">
          <v-checkbox :label="t('meteringPointLabels.useBillingAddress')"
                      v-model="useBillingAddress"
                      color="dark-grey-2"
          />
        </v-col>

        <v-col cols="12" sm="4" class="formInput7">
          <TextField :label="t('meteringPointLabels.street')"
                      v-model="meteringPointModelLocal!.addressPostal.address"
                      :disabled="useBillingAddress"
                      :rules="[formValidation.formValidationRequired]"></TextField>
        </v-col>
        <v-col cols="12" sm="4" class="formInput5">
          <TextField :label="t('meteringPointLabels.addressExtra')"
                      v-model="meteringPointModelLocal!.addressPostal.addressExtra"
                      :disabled="useBillingAddress"
          ></TextField>
        </v-col>
        <v-col cols="12" sm="4" class="formInput4">
          <TextField :label="t('meteringPointLabels.zipCode')"
                      v-model="meteringPointModelLocal!.addressPostal.zip"
                      :disabled="useBillingAddress"
                      :rules="[formValidation.formValidationZipCode]"></TextField>
        </v-col>
        <v-col cols="12" sm="5" class="formInput8">
          <TextField :label="t('meteringPointLabels.city')"
                      v-model="meteringPointModelLocal!.addressPostal.city"
                      :disabled="useBillingAddress"
                      :rules="[formValidation.formValidationRequired]"></TextField>
        </v-col>

        <v-col cols="12" sm="6" class="formInput8" v-if="multiDsoMode">
          <v-select
            :label="t('meteringPointLabels.gridOperator')"
            variant="outlined"
            color="dark-grey-2"
            :items="itemsDso"
            :disabled="!itemsDsoSelectable"
            v-model="meteringPointModelLocal!.ecnDso"
            :rules="[formValidation.formValidationRequired]"
          ></v-select>
        </v-col>


        <v-col cols="12" sm="4" class="formInput4" v-if="modeProducer">
          <v-select
            :label="t('meteringPointLabels.generationType')"
            variant="outlined"
            color="dark-grey-2"
            :items="itemsGenerationType"
            v-model="meteringPointModelLocal!.plant.generationType"
            :rules="[formValidation.formValidationRequired]"

          ></v-select>
        </v-col>
        <v-col cols="12" sm="4" class="formInput4" v-if="modeProducer">
          <TextField :label="t('meteringPointLabels.bottleneckCapacity')"
                      :hint="t('lineCapacityHint')"
                      :rules="[formValidation.formValidationIsNaturalNumber]"
                      v-model="meteringPointModelLocal!.plant.lineCapacity"
                      suffix="kWp"
          ></TextField>
        </v-col>
        <v-col cols="12" sm="4" class="formInput4" v-if="modeProducer">
          <v-select
            :label="t('meteringPointLabels.connectionType')"
            variant="outlined"
            color="dark-grey-2"
            :items="[CONNECTION_TYPE.SURPLUS_FEEDER, CONNECTION_TYPE.FULL_FEEDER]"
            v-model="meteringPointModelLocal!.plant.generationConfiguration"
            :rules="[formValidation.formValidationRequired]"
          ></v-select>
        </v-col>
        <v-col cols="12" sm="4" class="formInput4" v-if="modeProducer">
          <TextField :label="t('meteringPointLabels.surplusPerYear')" suffix="kWh"
                      :rules="[formValidation.formValidationIsNaturalNumber]"
                      v-model="meteringPointModelLocal!.assumptionEnergyPerYearKwh"
          ></TextField>
        </v-col>
        <v-col cols="12" sm="4" class="formInput4" v-if="modeConsumer">
          <TextField :label="t('meteringPointLabels.annualDemand')" suffix="kWh"
                      :rules="[formValidation.formValidationIsNaturalNumber]"
                      v-model="meteringPointModelLocal!.assumptionEnergyPerYearKwh"
          ></TextField>
        </v-col>

        <v-col cols="12" class="formInput12" v-if="modeProducer">
          <v-checkbox :label="t('meteringPointLabels.electricalStorage')"
                      v-model="haveStorage"
                      color="dark-grey-2"
          />
        </v-col>

        <v-col cols="12" sm="4" class="formInput4" v-if="modeProducer && haveStorage">
          <TextField :label="t('meteringPointLabels.storageSize')" suffix="kWh"
                      :rules="[formValidation.formValidationRequired, formValidation.formValidationIsNaturalNumber]"
                      v-model="meteringPointModelLocal!.energyStorageKwh"
          ></TextField>
        </v-col>
      </v-row>

    </v-col>
  </v-row>
</template>

<style scoped>

.indexContainer {

}

.indexIcon {
  display: flex;
  width: 50px;
  height: 50px;
  padding: 10px 12.5px;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  gap: 10px;
  margin-bottom: 20px;
  margin-left: 0;

  border-radius: 100px;
  border: 1px solid rgb(var(--v-theme-dark-grey-2));
  background: rgb(var(--v-theme-white));

  /* default */
  box-shadow: 0px 1px 5px 0px rgba(0, 0, 0, 0.12), 0px 2px 2px 0px rgba(0, 0, 0, 0.14), 0px 3px 1px -2px rgba(0, 0, 0, 0.20);
}

:deep(.v-icon) {
  color: black;
  opacity: 1 !important;
}
</style>
